import { cn } from '@/lib/cn';

export const BETSLIP_QUICK_STAKES = [5, 10, 25, 50] as const;

export interface BetslipStakeBarProps {
  amount: string;
  currencySuffix?: string;
  selectedQuickStake?: number | null;
  onAmountChange?: (value: string) => void;
  onQuickStake?: (value: number) => void;
  onMax?: () => void;
  /** When a FreeBet / BackupBet reward locks the stake. */
  disabled?: boolean;
  className?: string;
}

/**
 * Figma BetslipStakeBar — stake field with MAX + quick-stake pills.
 */
export function BetslipStakeBar({
  amount,
  currencySuffix,
  selectedQuickStake = null,
  onAmountChange,
  onQuickStake,
  onMax,
  disabled = false,
  className,
}: BetslipStakeBarProps) {
  const display =
    currencySuffix && amount !== '' ? `${amount} ${currencySuffix}` : amount;

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center gap-2',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
    >
      <div className="flex h-11 w-full items-center gap-1.5 overflow-hidden rounded-md border border-border-subtle bg-bg-surface py-1.5 pr-1.5 pl-3">
        <input
          type="text"
          inputMode="decimal"
          aria-label="Stake"
          disabled={disabled}
          value={display}
          onChange={(event) => {
            const raw = event.target.value;
            const stripped = currencySuffix
              ? raw.replace(new RegExp(`\\s*${escapeRegExp(currencySuffix)}\\s*$`), '').trim()
              : raw;
            onAmountChange?.(stripped);
          }}
          className="min-w-0 flex-1 bg-transparent text-right text-body-l font-medium text-text-primary outline-none disabled:cursor-not-allowed"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={onMax}
          className="shrink-0 rounded-sm bg-bg-elevated px-3 py-2 text-caption font-bold text-text-primary transition-colors hover:bg-bg-canvas disabled:opacity-60"
        >
          MAX
        </button>
      </div>

      <div className="flex w-full items-center gap-2">
        {BETSLIP_QUICK_STAKES.map((value) => {
          const selected = selectedQuickStake === value;
          return (
            <button
              key={value}
              type="button"
              disabled={disabled}
              onClick={() => onQuickStake?.(value)}
              className={cn(
                'flex flex-1 items-center justify-center rounded-full px-2 py-2.5 text-body-m font-bold transition-colors',
                selected
                  ? 'bg-action-primary text-text-on-action'
                  : 'bg-bg-elevated text-text-primary hover:bg-bg-canvas',
                disabled && 'opacity-60',
              )}
            >
              {value}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
